import {FC, useEffect, useState} from 'react'
import {api, ApiError, AuditLogRow, EmailLogRow, EmailStatus} from '../../services/api'
import {PageHead} from '../renter/ui'
import {Spinner} from '../../components/Spinner'
import {useToast} from '../../components/Toast'
import {useIsMobile} from '../../hooks/useIsMobile'

const INK = '#1b1b29'
const MUTED = '#7e8299'
const ACCENT = '#039540'

const inp: React.CSSProperties = {
  padding: '9px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: 9,
  fontSize: 14,
  background: '#fff',
  boxSizing: 'border-box',
  outline: 'none',
}

const fmt = (iso: string): string => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, {dateStyle: 'medium', timeStyle: 'medium'})
}

/** Colour the outcome so failures stand out when scanning. */
const StatusPill: FC<{code: number | null}> = ({code}) => {
  const ok = code != null && code < 400
  const tone = code == null
    ? {color: MUTED, bg: 'rgba(126,130,153,0.14)'}
    : ok
      ? {color: '#1f9d5b', bg: 'rgba(3,149,64,0.12)'}
      : {color: '#d9214e', bg: 'rgba(217,33,78,0.1)'}
  return (
    <span style={{fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '3px 9px', color: tone.color, background: tone.bg}}>
      {code ?? '—'}
    </span>
  )
}

const EMAIL_TONE: Record<EmailStatus, {color: string; bg: string; label: string}> = {
  DELIVERED: {color: '#1f9d5b', bg: 'rgba(3,149,64,0.12)', label: 'Delivered'},
  SENT: {color: '#b8770a', bg: 'rgba(255,168,0,0.15)', label: 'Sent'},
  FAILED: {color: '#d9214e', bg: 'rgba(217,33,78,0.1)', label: 'Failed'},
  BOUNCED: {color: '#d9214e', bg: 'rgba(217,33,78,0.1)', label: 'Bounced'},
  COMPLAINED: {color: '#d9214e', bg: 'rgba(217,33,78,0.1)', label: 'Spam report'},
}

/** "Sent" means accepted by the provider; "Delivered" means it arrived. */
const EmailPill: FC<{status: EmailStatus}> = ({status}) => {
  const t = EMAIL_TONE[status] ?? EMAIL_TONE.SENT
  return (
    <span style={{fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '3px 9px', color: t.color, background: t.bg}}>
      {t.label}
    </span>
  )
}

const EmailsView: FC = () => {
  const toast = useToast()
  const [rows, setRows] = useState<EmailLogRow[]>([])
  const [loading, setLoading] = useState(true)
  const [to, setTo] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  useEffect(() => {
    let alive = true
    setLoading(true)
    api
      .emailLogs(to || undefined, 150, dateFrom || undefined, dateTo || undefined)
      .then((r) => alive && setRows(r))
      .catch((e) => toast.error(e instanceof ApiError ? e.message : 'Could not load the email log'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, dateFrom, dateTo])

  return (
    <div style={{background: '#fff', borderRadius: 16, boxShadow: '0 6px 18px rgba(27,27,41,0.06)', overflow: 'hidden'}}>
      <div style={{padding: '16px 18px', borderBottom: '1px solid #f1f3f7', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center'}}>
        <input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder='Filter by recipient email…'
          style={{...inp, flex: '2 1 240px', maxWidth: 340}}
        />
        <input type='date' value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)}
          title='From this day' style={{...inp, flex: '0 1 160px'}} />
        <input type='date' value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)}
          title='To this day' style={{...inp, flex: '0 1 160px'}} />
        {(dateFrom || dateTo) && (
          <button type='button' onClick={() => { setDateFrom(''); setDateTo('') }}
            style={{border: 'none', background: 'none', color: MUTED, cursor: 'pointer', fontSize: 13}}>Clear dates</button>
        )}
      </div>
      {loading && rows.length === 0 ? (
        <div style={{padding: 28, textAlign: 'center'}}><Spinner size={20} /></div>
      ) : rows.length === 0 ? (
        <div style={{padding: 28, textAlign: 'center', color: MUTED, fontSize: 14}}>
          No emails recorded yet — this starts from the next message the site sends.
        </div>
      ) : (
        <div style={{overflowX: 'auto'}}>
          <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 13.5}}>
            <thead>
              <tr style={{background: '#fafbfc', color: MUTED, textAlign: 'left'}}>
                <th style={{padding: '11px 14px', fontWeight: 700}}>When</th>
                <th style={{padding: '11px 14px', fontWeight: 700}}>To</th>
                <th style={{padding: '11px 14px', fontWeight: 700}}>Subject</th>
                <th style={{padding: '11px 14px', fontWeight: 700}}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{borderTop: '1px solid #f1f3f7'}}>
                  <td style={{padding: '11px 14px', color: MUTED, whiteSpace: 'nowrap'}}>{fmt(r.createdAt)}</td>
                  <td style={{padding: '11px 14px', color: INK}}>{r.toAddress}</td>
                  <td style={{padding: '11px 14px', color: INK}}>
                    {r.subject}
                    {r.attachmentCount > 0 && <span style={{color: MUTED}}> · {r.attachmentCount} attachment{r.attachmentCount === 1 ? '' : 's'}</span>}
                    {r.reason && <div style={{color: '#d9214e', fontSize: 12.5, marginTop: 2}}>{r.reason}</div>}
                  </td>
                  <td style={{padding: '11px 14px'}}><EmailPill status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/**
 * Admin audit trail — who changed what, from which address, and when.
 * Read-only by design: the value of a log is that nobody can edit it.
 */
const AuditLogPage: FC = () => {
  const toast = useToast()
  const isMobile = useIsMobile()
  const [rows, setRows] = useState<AuditLogRow[]>([])
  const [actions, setActions] = useState<string[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState<AuditLogRow | null>(null)
  const [tab, setTab] = useState<'changes' | 'emails'>('changes')

  const [search, setSearch] = useState('')
  const [action, setAction] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const pageSize = 50
  const pages = Math.max(1, Math.ceil(total / pageSize))

  useEffect(() => {
    api.auditActions().then(setActions).catch(() => {})
  }, [])

  useEffect(() => {
    let alive = true
    setLoading(true)
    api
      .auditLogs({search, action, from, to, page, pageSize})
      .then((res) => {
        if (!alive) return
        setRows(res.rows)
        setTotal(res.total)
      })
      .catch((e) => toast.error(e instanceof ApiError ? e.message : 'Could not load the audit log'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, action, from, to, page])

  const resetTo = (fn: () => void) => {
    setPage(1)
    fn()
  }

  return (
    <div>
      <PageHead
        icon='🧾'
        eyebrow='Account'
        title='Audit log'
        sub='Every change made through the site, and every email it sent.'
      />

      <div style={{display: 'flex', gap: 8, marginBottom: 16}}>
        {([['changes', 'Changes'], ['emails', 'Emails']] as const).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            style={{
              fontWeight: 700, fontSize: 14, borderRadius: 999, padding: '8px 18px', cursor: 'pointer',
              border: tab === k ? 'none' : '1px solid #e5e7eb',
              color: tab === k ? '#fff' : INK,
              background: tab === k ? ACCENT : '#fff',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'emails' ? <EmailsView /> : <>

      <div style={{background: '#fff', borderRadius: 16, padding: '18px 18px 8px', boxShadow: '0 6px 18px rgba(27,27,41,0.06)', marginBottom: 18}}>
        <div style={{display: 'flex', flexWrap: 'wrap', gap: 10}}>
          <input
            value={search}
            onChange={(e) => resetTo(() => setSearch(e.target.value))}
            placeholder='Search email, action, address…'
            style={{...inp, flex: '2 1 240px'}}
          />
          <select value={action} onChange={(e) => resetTo(() => setAction(e.target.value))} style={{...inp, flex: '1 1 180px'}}>
            <option value=''>All actions</option>
            {actions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <input type='date' value={from} onChange={(e) => resetTo(() => setFrom(e.target.value))} style={{...inp, flex: '0 1 160px'}} />
          <input type='date' value={to} onChange={(e) => resetTo(() => setTo(e.target.value))} style={{...inp, flex: '0 1 160px'}} />
        </div>
        <div style={{fontSize: 12.5, color: MUTED, margin: '10px 2px 12px'}}>
          {loading ? 'Loading…' : `${total.toLocaleString()} record${total === 1 ? '' : 's'}`}
        </div>
      </div>

      <div style={{background: '#fff', borderRadius: 16, boxShadow: '0 6px 18px rgba(27,27,41,0.06)', overflow: 'hidden'}}>
        {loading && rows.length === 0 ? (
          <div style={{padding: 28, textAlign: 'center'}}><Spinner size={20} /></div>
        ) : rows.length === 0 ? (
          <div style={{padding: 28, textAlign: 'center', color: MUTED, fontSize: 14}}>
            Nothing recorded for these filters yet.
          </div>
        ) : isMobile ? (
          rows.map((r) => (
            <div key={r.id} onClick={() => setOpen(r)} style={{borderTop: '1px solid #f1f3f7', padding: '14px 16px', cursor: 'pointer'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', gap: 10}}>
                <strong style={{color: INK, fontSize: 14}}>{r.action}</strong>
                <StatusPill code={r.statusCode} />
              </div>
              <div style={{color: MUTED, fontSize: 12.5, marginTop: 4}}>
                {r.actorEmail ?? 'signed out'} · {r.ip ?? 'no address'}
              </div>
              <div style={{color: MUTED, fontSize: 12.5}}>{fmt(r.createdAt)}</div>
            </div>
          ))
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 13.5}}>
              <thead>
                <tr style={{background: '#fafbfc', color: MUTED, textAlign: 'left'}}>
                  <th style={{padding: '11px 14px', fontWeight: 700}}>When</th>
                  <th style={{padding: '11px 14px', fontWeight: 700}}>Who</th>
                  <th style={{padding: '11px 14px', fontWeight: 700}}>Action</th>
                  <th style={{padding: '11px 14px', fontWeight: 700}}>From</th>
                  <th style={{padding: '11px 14px', fontWeight: 700}}>Result</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setOpen(r)}
                    style={{borderTop: '1px solid #f1f3f7', cursor: 'pointer'}}
                  >
                    <td style={{padding: '11px 14px', color: MUTED, whiteSpace: 'nowrap'}}>{fmt(r.createdAt)}</td>
                    <td style={{padding: '11px 14px', color: INK}}>
                      {r.actorEmail ?? <span style={{color: MUTED}}>signed out</span>}
                      {r.actorRole && <div style={{color: MUTED, fontSize: 12}}>{r.actorRole}</div>}
                    </td>
                    <td style={{padding: '11px 14px', color: INK, fontWeight: 600}}>{r.action}</td>
                    <td style={{padding: '11px 14px', color: MUTED, whiteSpace: 'nowrap'}}>{r.ip ?? '—'}</td>
                    <td style={{padding: '11px 14px'}}><StatusPill code={r.statusCode} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #f1f3f7'}}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              style={{fontSize: 13.5, fontWeight: 600, color: page <= 1 ? MUTED : ACCENT, background: 'none', border: 'none', cursor: page <= 1 ? 'not-allowed' : 'pointer'}}
            >
              ‹ Previous
            </button>
            <span style={{fontSize: 13, color: MUTED}}>Page {page} of {pages}</span>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              style={{fontSize: 13.5, fontWeight: 600, color: page >= pages ? MUTED : ACCENT, background: 'none', border: 'none', cursor: page >= pages ? 'not-allowed' : 'pointer'}}
            >
              Next ›
            </button>
          </div>
        )}
      </div>

      </>}

      {open && (
        <div
          onClick={() => setOpen(null)}
          style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '50px 20px', overflowY: 'auto'}}
        >
          <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, padding: '24px', width: '100%', maxWidth: 560}}>
            <h2 style={{fontSize: 18, fontWeight: 800, color: INK, margin: '0 0 14px'}}>{open.action}</h2>
            {[
              ['When', fmt(open.createdAt)],
              ['Who', open.actorEmail ?? 'signed out'],
              ['Role', open.actorRole ?? '—'],
              ['From', open.ip ?? '—'],
              ['Request', `${open.method} ${open.path}`],
              ['Result', String(open.statusCode ?? '—')],
              ['Record', open.entityId ?? '—'],
            ].map(([k, v]) => (
              <div key={k} style={{display: 'flex', gap: 12, padding: '6px 0', fontSize: 13.5}}>
                <span style={{color: MUTED, minWidth: 90}}>{k}</span>
                <span style={{color: INK, wordBreak: 'break-all'}}>{v}</span>
              </div>
            ))}
            {open.details && (
              <>
                <div style={{color: MUTED, fontSize: 13.5, margin: '12px 0 6px'}}>What was sent</div>
                <pre style={{background: '#f7f8fa', borderRadius: 10, padding: 12, fontSize: 12, overflowX: 'auto', margin: 0}}>
                  {JSON.stringify(open.details, null, 2)}
                </pre>
              </>
            )}
            <button
              onClick={() => setOpen(null)}
              style={{marginTop: 18, fontWeight: 700, fontSize: 14, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '10px 20px', cursor: 'pointer'}}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export {AuditLogPage}
